import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { CheckCircle2, XCircle, CalendarOff } from 'lucide-react';

import { useChartTheme } from '../../../../utils/chartTheme';
import { getAttendanceReport } from '../../../../services/StudentDashboardService';
import useScrollLoadMore from '../../common/useScrollLoadMore';
import ClassDetailPopup from './ClassDetailPopup';
import '../../Trainer/TrainerDashboard.css';
import '../../common/lms-pages.css';
import './attendance-report.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/* =============================================================
   /my-attendance-report -- the student's own attendance report.

   Every class held for the student's batches, present AND absent,
   with the figures, the trend over time and a way to narrow it by
   date, batch, trainer and course.

   WHAT THIS PAGE DOES NOT DO: work anything out. Present or absent,
   the totals, the percentage, the chart's points, the filter options
   and the paging all arrive from GET /studentdashboard/attendance-report
   (studentdashboard.service.js AttendanceReport), already decided,
   over EVERY matching session -- not the page of rows in hand. The
   filters here are only what is sent; nothing narrows what arrives.

   The list loads as it is scrolled (useScrollLoadMore), a page at a
   time from the server; a filter change starts it again from page 1.
   ============================================================= */

const PAGE_SIZE = 10;

const EMPTY_FILTERS = {
  dateFrom: '',
  dateTo: '',
  batchId: '',
  courseId: '',
  trainerId: '',
  status: '',
  granularity: '',
};

const STATUS = {
  present: { label: 'Present', pill: 'pg-pill--good', Icon: CheckCircle2 },
  absent: { label: 'Not present', pill: 'pg-pill--bad', Icon: XCircle },
  commonOff: { label: 'No class (common off)', pill: '', Icon: CalendarOff },
};

const GRANULARITY = [
  { key: '', label: 'Auto' },
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const pct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`);

/** The attendance line(s): one for all, and one per batch when the API sends them. */
const AttendanceTrendChart = ({ trend }) => {
  const theme = useChartTheme();
  const labels = useMemo(() => trend?.labels || [], [trend]);
  const series = useMemo(() => trend?.series || [], [trend]);

  const data = useMemo(
    () => ({
      labels,
      datasets: series.map((s, i) => {
        // "All" in the accent and filled; each batch a line of its own slot.
        const colour = theme.series[i % theme.series.length];
        const isAll = i === 0;
        return {
          label: s.label,
          data: s.points.map((p) => p.percentage),
          borderColor: colour,
          backgroundColor: isAll ? theme.wash(colour, 0.1) : colour,
          borderWidth: isAll ? 2.5 : 1.75,
          borderDash: isAll ? [] : [5, 4],
          pointRadius: isAll ? 4 : 3,
          pointHoverRadius: 6,
          pointBackgroundColor: colour,
          pointBorderColor: theme.surface,
          pointBorderWidth: 2,
          tension: 0.3,
          fill: isAll,
          // A period with no class for this batch is a gap, not a zero.
          spanGaps: true,
        };
      }),
    }),
    [labels, series, theme],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: series.length > 1,
          position: 'top',
          align: 'end',
          labels: theme.legendLabels(),
        },
        tooltip: {
          ...theme.tooltip,
          displayColors: series.length > 1,
          callbacks: {
            label: (ctx) => {
              const p = series[ctx.datasetIndex]?.points?.[ctx.dataIndex];
              if (!p || !p.total) return null;
              const name = series.length > 1 ? `${series[ctx.datasetIndex].label}: ` : '';
              return ` ${name}${pct(p.percentage)} — ${p.present} of ${p.total} classes`;
            },
          },
        },
      },
      scales: {
        x: theme.categoryAxis({ ticks: { autoSkip: true, maxRotation: 0 } }),
        y: theme.valueAxis({ min: 0, max: 100, ticks: { stepSize: 25, callback: (v) => `${v}%` } }),
      },
    }),
    [series, theme],
  );

  if (!labels.length) {
    return <p className="br-chart-state">No classes to chart for these filters.</p>;
  }
  return (
    <div className="br-chart-plot arp-plot" role="img" aria-label="Attendance over time">
      <Line data={data} options={options} />
    </div>
  );
};

const StudentAttendanceReport = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  // The figures, the chart and the filter options: from page 1's answer,
  // which describes every matching class. Later pages only add rows.
  const [report, setReport] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const listEndRef = useRef(null);
  // The class open in the view popup (its id), or null.
  const [viewingId, setViewingId] = useState(null);
  // Which request an answer belongs to: a slow one must not land on top of
  // a filter chosen after it.
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    const append = page > 1;
    if (append) setLoadingMore(true);
    else setLoading(true);
    setFailed(false);
    // Only what is set is sent: an empty `batchId=` is a filter the server
    // would have to decide to ignore.
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    getAttendanceReport({ ...params, page, limit: PAGE_SIZE })
      .then((res) => {
        if (mine !== seq.current) return;
        const list = Array.isArray(res.items) ? res.items : [];
        if (!append) setReport(res);
        setTotal(Number(res.pagination?.total) || 0);
        setRows((prev) => {
          if (!append) return list;
          // A class added between two pages pushes the boundary along and
          // repeats a row; drop any id already on screen.
          const seen = new Set(prev.map((r) => String(r.id)));
          return [...prev, ...list.filter((r) => !seen.has(String(r.id)))];
        });
      })
      .catch(() => {
        if (mine !== seq.current) return;
        if (!append) setFailed(true);
      })
      .finally(() => {
        if (mine !== seq.current) return;
        setLoading(false);
        setLoadingMore(false);
      });
  }, [filters, page]);

  const hasMore = rows.length < total;

  /* The next page when the foot of the list comes into view. Rebuilt on
     every batch (rows.length, loadingMore): an observer that is not
     reconnected after a page lands never reports the sentinel again. */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore || failed || !hasMore) return;
      setPage((p) => p + 1);
    },
    [rows.length, total, loading, loadingMore, failed],
  );

  const set = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };
  const anyFilter = Object.entries(filters).some(([k, v]) => k !== 'granularity' && v);
  const clear = () => {
    setFilters((prev) => ({ ...EMPTY_FILTERS, granularity: prev.granularity }));
    setPage(1);
  };

  const summary = report?.summary || {};
  const options = report?.options || { batches: [], courses: [], trainers: [] };

  return (
    <div className="dashboard-wrapper">
      <div className="pg arp">
        <header className="pg-head">
          <div className="pg-head-main">
            <h1 className="pg-title">My Attendance Report</h1>
            <p className="pg-sub">
              Every class held for your batches — the ones you attended and the ones you missed.
            </p>
          </div>
        </header>

        {/* The figures first. Every one is the API's own, over every class
            the filters leave -- an em dash where there is nothing to count,
            never a zero standing in for "not known". */}
        <div className="pgm-tiles arp-tiles">
          <div className={`pgm-tile${summary.percentage >= 75 ? ' pgm-tile--good' : summary.percentage !== null && summary.percentage !== undefined ? ' pgm-tile--bad' : ''}`}>
            <b>{pct(summary.percentage)}</b>
            <span>Attendance</span>
          </div>
          <div className="pgm-tile pgm-tile--good">
            <b>{summary.present ?? '—'}</b>
            <span>Present</span>
          </div>
          <div className="pgm-tile pgm-tile--bad">
            <b>{summary.absent ?? '—'}</b>
            <span>Not present</span>
          </div>
          <div className="pgm-tile">
            <b>{summary.sessions ?? '—'}</b>
            <span>
              Classes held
              {summary.commonOff ? ` · ${summary.commonOff} common off` : ''}
            </span>
          </div>
        </div>

        <section className="pg-filters arp-filters" aria-label="Filter your attendance">
          <div className="arp-filters-fields">
            <div className="pg-field">
              <label className="pg-label" htmlFor="arp-from">From</label>
              <input
                id="arp-from"
                type="date"
                className="pg-input"
                value={filters.dateFrom}
                max={filters.dateTo || undefined}
                onChange={(e) => set('dateFrom', e.target.value)}
              />
            </div>
            <div className="pg-field">
              <label className="pg-label" htmlFor="arp-to">To</label>
              <input
                id="arp-to"
                type="date"
                className="pg-input"
                value={filters.dateTo}
                min={filters.dateFrom || undefined}
                onChange={(e) => set('dateTo', e.target.value)}
              />
            </div>
            <div className="pg-field">
              <label className="pg-label" htmlFor="arp-batch">Batch</label>
              <select id="arp-batch" className="pg-select" value={filters.batchId} onChange={(e) => set('batchId', e.target.value)}>
                <option value="">All batches</option>
                {options.batches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <div className="pg-field">
              <label className="pg-label" htmlFor="arp-course">Course</label>
              <select id="arp-course" className="pg-select" value={filters.courseId} onChange={(e) => set('courseId', e.target.value)}>
                <option value="">All courses</option>
                {options.courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="pg-field">
              <label className="pg-label" htmlFor="arp-trainer">Trainer</label>
              <select id="arp-trainer" className="pg-select" value={filters.trainerId} onChange={(e) => set('trainerId', e.target.value)}>
                <option value="">All trainers</option>
                {options.trainers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="pg-field">
              <label className="pg-label" htmlFor="arp-status">Show</label>
              <select id="arp-status" className="pg-select" value={filters.status} onChange={(e) => set('status', e.target.value)}>
                <option value="">Present and not present</option>
                <option value="present">Present only</option>
                <option value="absent">Not present only</option>
              </select>
            </div>
          </div>
          {anyFilter && (
            <div className="pg-filters-actions">
              <button type="button" className="pg-btn" onClick={clear}>Clear filters</button>
            </div>
          )}
        </section>

        <section className="br-chart arp-chart" aria-label="Your attendance over time">
          <header className="br-chart-head">
            <div>
              <h5 className="br-chart-title">Your attendance over time</h5>
              <p className="br-chart-sub">
                The share of classes you attended in each {report?.trend?.granularity || 'period'}
                {(report?.trend?.series || []).length > 1 ? ', overall and for each batch' : ''}.
              </p>
            </div>
            <div className="br-seg" role="group" aria-label="Group the chart by">
              {GRANULARITY.map((g) => (
                <button
                  type="button"
                  key={g.key || 'auto'}
                  aria-pressed={filters.granularity === g.key}
                  className={`br-seg-btn${filters.granularity === g.key ? ' is-on' : ''}`}
                  onClick={() => set('granularity', g.key)}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </header>
          {loading && !report ? (
            <p className="br-chart-state">Loading…</p>
          ) : (
            <AttendanceTrendChart trend={report?.trend} />
          )}
        </section>

        <section className="arp-list-wrap" aria-label="Your classes">
          <header className="arp-list-head">
            <h2 className="arp-list-title">Classes</h2>
            <span className="arp-list-count">
              {total} {total === 1 ? 'class' : 'classes'}
            </span>
          </header>

          {loading && !report ? (
            <div className="pg-state">
              <span className="pg-spinner" role="status" aria-hidden="true" />
              <p className="pg-state-text">Loading your attendance...</p>
            </div>
          ) : failed ? (
            <p className="pgm-empty">Your attendance did not load. Try again in a moment.</p>
          ) : rows.length === 0 ? (
            <p className="pgm-empty">
              {anyFilter ? 'No class matches these filters.' : 'No classes have been held for your batches yet.'}
            </p>
          ) : (
            <ul className={`arp-list${loading ? ' is-busy' : ''}`}>
              {rows.map((r) => {
                const s = STATUS[r.status] || STATUS.absent;
                const { Icon } = s;
                return (
                  <li
                    key={r.id}
                    className={`arp-item arp-item--${r.status}`}
                    // The whole row opens the class (view_popup_rules).
                    role="button"
                    tabIndex={0}
                    aria-haspopup="dialog"
                    aria-label={`Open the class on ${r.date ? moment(r.date).format('D MMM YYYY') : 'this date'}`}
                    onClick={() => setViewingId(r.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setViewingId(r.id);
                      }
                    }}
                  >
                    <span className="arp-item-mark" aria-hidden="true">
                      <Icon />
                    </span>
                    <div className="arp-item-main">
                      <div className="arp-item-top">
                        <span className="arp-item-date">
                          {r.date ? moment(r.date).format('ddd, D MMM YYYY') : 'Date not recorded'}
                        </span>
                        <span className={`pg-pill ${s.pill}`}>{s.label}</span>
                      </div>
                      <span className="arp-item-batch">{r.batchName}</span>
                      <span className="arp-item-meta">
                        {[r.courseName, r.trainerName ? `Taught by ${r.trainerName}` : null].filter(Boolean).join(' · ') || '—'}
                      </span>
                      {Array.isArray(r.topics) && r.topics.length > 0 && (
                        <span className="arp-item-topics">
                          {r.topics.length === 1 ? 'Topic' : `${r.topics.length} topics`}: {r.topics.slice(0, 3).join(', ')}
                          {r.topics.length > 3 ? `, +${r.topics.length - 3} more` : ''}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Where the reader is, and the sentinel the observer watches. */}
          <div className="pg-scroll-foot" ref={listEndRef}>
            {hasMore ? (
              <>
                <span className="pg-scroll-count">
                  {rows.length} of {total}
                </span>
                {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
              </>
            ) : total > 0 ? (
              <span className="pg-scroll-count">
                All {total} {total === 1 ? 'class' : 'classes'} loaded
              </span>
            ) : null}
          </div>
        </section>
      </div>

      {viewingId && <ClassDetailPopup id={viewingId} onClose={() => setViewingId(null)} />}
    </div>
  );
};

export default StudentAttendanceReport;
